import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    LogIn,
    LayoutDashboard,
    ShieldCheck,
    Phone,
    Award,
    Building2,
    ZoomIn,
} from "lucide-react";
import PublicNav from "@/components/PublicNav";

const publicApi = axios.create({ baseURL: API });

const initials = (p) =>
    `${p.first_name?.[0] || ""}${p.last_name?.[0] || ""}`.toUpperCase() || "?";

export default function PublicPersons() {
    const { token } = useAuth();
    const [persons, setPersons] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [orgFilter, setOrgFilter] = useState("all");
    const [showDeceased, setShowDeceased] = useState(true);
    const [viewing, setViewing] = useState(null);

    useEffect(() => {
        publicApi
            .get("/public/persons")
            .then((r) => setPersons(r.data))
            .finally(() => setLoading(false));
    }, []);

    const organizations = useMemo(() => {
        const set = new Set();
        for (const p of persons) if (p.organization) set.add(p.organization);
        return [...set].sort((a, b) => a.localeCompare(b));
    }, [persons]);

    const filtered = persons.filter((p) => {
        const q = search.trim().toLowerCase();
        const hay =
            `${p.first_name} ${p.last_name} ${p.alias || ""} ${p.phone || ""} ${p.rank || ""}`.toLowerCase();
        const matchSearch = !q || hay.includes(q);
        const matchOrg =
            orgFilter === "all" ||
            (orgFilter === "__none__" && !p.organization) ||
            p.organization === orgFilter;
        const matchDeceased = showDeceased || !p.deceased;
        return matchSearch && matchOrg && matchDeceased;
    });

    const grouped = useMemo(() => {
        const groups = {};
        for (const p of filtered) {
            const key = p.organization || "Sans organisation";
            (groups[key] ||= []).push(p);
        }
        for (const k of Object.keys(groups)) {
            groups[k].sort((a, b) => {
                const ra = (a.rank || "").toLowerCase();
                const rb = (b.rank || "").toLowerCase();
                if (!ra && rb) return 1;
                if (ra && !rb) return -1;
                if (ra !== rb) return ra.localeCompare(rb);
                return (a.last_name || "").localeCompare(b.last_name || "");
            });
        }
        return groups;
    }, [filtered]);

    return (
        <div className="min-h-screen bg-[#F7F3E8]">
            <header className="sticky top-0 z-20 bg-[#2E3A22] text-[#EDE7D3] border-b border-[#3D4E2E] shadow-sm">
                <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between gap-4 flex-wrap">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.25em] text-[#C8A75C] font-semibold">
                            Raza Perdida MC
                        </div>
                        <div
                            data-testid="public-brand"
                            className="font-mc text-xl sm:text-2xl font-semibold tracking-tight mt-0.5"
                        >
                            Personnes
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <PublicNav />
                        {token ? (
                            <Link to="/dashboard">
                                <Button
                                    data-testid="public-open-dashboard-btn"
                                    className="bg-[#F5EFDA] hover:bg-[#EDE4C0] text-[#2E3A22]"
                                >
                                    <LayoutDashboard className="w-4 h-4 mr-2" />
                                    Tableau de bord
                                </Button>
                            </Link>
                        ) : (
                            <Link to="/login">
                                <Button
                                    data-testid="public-login-btn"
                                    className="bg-[#B8934A] hover:bg-[#A07E36] text-white"
                                >
                                    <LogIn className="w-4 h-4 mr-2" />
                                    Accès membre
                                </Button>
                            </Link>
                        )}
                    </div>
                </div>
            </header>

            <main className="max-w-6xl mx-auto px-6 py-10 space-y-8">
                {!token && (
                    <div className="flex items-center gap-3 text-sm text-[#5A6F45] bg-[#EDE7D3] border border-[#DDD3B9] rounded-md px-4 py-3">
                        <ShieldCheck className="w-4 h-4 shrink-0" />
                        <span>
                            Vue publique — fiches consultables en lecture seule. Connectez-vous pour éditer.
                        </span>
                    </div>
                )}

                <div className="flex gap-3 flex-wrap items-center">
                    <Input
                        data-testid="public-persons-search"
                        placeholder="Rechercher (nom, surnom, tél, rang)…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="max-w-sm h-11 bg-white"
                    />
                    <Select value={orgFilter} onValueChange={setOrgFilter}>
                        <SelectTrigger
                            data-testid="public-persons-org-filter"
                            className="w-64 h-11 bg-white"
                        >
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Toutes organisations</SelectItem>
                            {organizations.map((o) => (
                                <SelectItem key={o} value={o}>
                                    {o}
                                </SelectItem>
                            ))}
                            <SelectItem value="__none__">Sans organisation</SelectItem>
                        </SelectContent>
                    </Select>
                    <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer select-none px-3 py-2 rounded-md border border-slate-200 bg-white hover:bg-slate-50">
                        <input
                            type="checkbox"
                            checked={showDeceased}
                            onChange={(e) => setShowDeceased(e.target.checked)}
                            className="h-4 w-4 accent-slate-900"
                            data-testid="public-persons-show-deceased-toggle"
                        />
                        Afficher les décédés
                    </label>
                </div>

                {loading ? (
                    <div className="text-slate-500 text-sm text-center py-10">
                        Chargement…
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="text-slate-500 text-sm text-center py-16 border border-dashed border-slate-200 rounded-lg bg-white">
                        Aucune fiche.
                    </div>
                ) : (
                    Object.keys(grouped)
                        .sort((a, b) => {
                            if (a === "Sans organisation") return 1;
                            if (b === "Sans organisation") return -1;
                            return a.localeCompare(b);
                        })
                        .map((org) => (
                            <section key={org} className="space-y-4">
                                <div className="flex items-baseline justify-between border-b border-slate-200 pb-2">
                                    <div className="flex items-center gap-2">
                                        <Building2 className="w-4 h-4 text-[#7A6018]" />
                                        <h2 className="font-display text-lg font-semibold text-slate-900">
                                            {org}
                                        </h2>
                                    </div>
                                    <span className="text-xs text-slate-500 font-medium">
                                        {grouped[org].length} membre
                                        {grouped[org].length > 1 ? "s" : ""}
                                    </span>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                                    {grouped[org].map((p) => (
                                        <Card
                                            key={p.id}
                                            data-testid={`public-person-card-${p.id}`}
                                            className={`border-slate-200 shadow-sm p-5 hover:shadow-md transition-shadow ${
                                                p.deceased
                                                    ? "bg-slate-100 border-l-4 border-l-slate-500 opacity-90"
                                                    : "bg-white"
                                            }`}
                                        >
                                            <div className="flex items-start gap-4">
                                                {p.photo_url ? (
                                                    <button
                                                        type="button"
                                                        onClick={() => setViewing(p)}
                                                        data-testid={`public-person-photo-${p.id}`}
                                                        className="relative group w-16 h-16 rounded-full overflow-hidden bg-[#DDE5D0] flex items-center justify-center shrink-0 border border-[#3D4E2E]/20 cursor-zoom-in focus:outline-none focus:ring-2 focus:ring-[#3D4E2E]/40"
                                                        aria-label="Voir la photo en grand"
                                                    >
                                                        <img
                                                            src={p.photo_url}
                                                            alt={p.first_name}
                                                            className={`w-full h-full object-cover transition-transform duration-200 group-hover:scale-105 ${p.deceased ? "grayscale" : ""}`}
                                                        />
                                                        <span className="absolute inset-0 bg-black/0 group-hover:bg-black/30 flex items-center justify-center transition-colors">
                                                            <ZoomIn className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                                                        </span>
                                                    </button>
                                                ) : (
                                                    <div className={`w-16 h-16 rounded-full bg-[#DDE5D0] text-[#3D4E2E] flex items-center justify-center shrink-0 border border-[#3D4E2E]/20 ${p.deceased ? "grayscale" : ""}`}>
                                                        <span className="font-display text-lg font-semibold">
                                                            {initials(p)}
                                                        </span>
                                                    </div>
                                                )}
                                                <div className="min-w-0 flex-1">
                                                    <div className="font-medium text-slate-900 truncate flex items-center gap-2">
                                                        {p.deceased && (
                                                            <span
                                                                title="Décédé(e)"
                                                                className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-slate-900 text-slate-100 text-xs font-bold"
                                                            >
                                                                †
                                                            </span>
                                                        )}
                                                        <span className={p.deceased ? "line-through decoration-slate-500 decoration-2 text-slate-700" : ""}>
                                                            {p.first_name} {p.last_name}
                                                        </span>
                                                    </div>
                                                    {p.alias && (
                                                        <div className="text-xs text-slate-500 italic truncate">
                                                            « {p.alias} »
                                                        </div>
                                                    )}
                                                    <div className="flex flex-wrap gap-1.5 mt-2">
                                                        {p.rank && (
                                                            <Badge className="bg-[#F0E5C7] text-[#7A6018] border border-[#E0D3A0] font-medium">
                                                                <Award className="w-3 h-3 mr-1" />
                                                                {p.rank}
                                                            </Badge>
                                                        )}
                                                    </div>
                                                    {p.phone && (
                                                        <div className="text-sm text-slate-700 mt-2 flex items-center gap-1.5">
                                                            <Phone className="w-3.5 h-3.5 text-slate-400" />
                                                            <span className="tabular-nums">
                                                                {p.phone}
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            {p.notes && (
                                                <div className="mt-3 pt-3 border-t border-slate-100 text-xs text-slate-600 whitespace-pre-wrap line-clamp-3">
                                                    {p.notes}
                                                </div>
                                            )}
                                        </Card>
                                    ))}
                                </div>
                            </section>
                        ))
                )}
            </main>

            <footer className="max-w-6xl mx-auto px-6 pb-10 text-center text-xs text-slate-500">
                Raza Perdida MC - Vue des personnes
            </footer>

            <Dialog open={!!viewing} onOpenChange={(v) => !v && setViewing(null)}>
                <DialogContent className="sm:max-w-2xl p-0 overflow-hidden bg-slate-950 border-slate-800">
                    {viewing?.photo_url && (
                        <img
                            src={viewing.photo_url}
                            alt={`${viewing.first_name} ${viewing.last_name}`}
                            className="w-full h-auto max-h-[80vh] object-contain bg-slate-950"
                        />
                    )}
                    {viewing && (
                        <div className="px-6 py-4 bg-slate-900 text-slate-100 border-t border-slate-800">
                            <div className="font-medium text-white">
                                {viewing.first_name} {viewing.last_name}
                            </div>
                            {(viewing.alias || viewing.rank || viewing.organization) && (
                                <div className="text-sm text-slate-400 mt-0.5">
                                    {viewing.alias && (
                                        <span className="italic">« {viewing.alias} »</span>
                                    )}
                                    {viewing.alias && (viewing.rank || viewing.organization) && (
                                        <span> · </span>
                                    )}
                                    {viewing.rank}
                                    {viewing.rank && viewing.organization && <span> · </span>}
                                    {viewing.organization}
                                </div>
                            )}
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
